import { useAppForm } from '@/components/form';
import { AbsatzVorgabe } from '@/domain/controlling/entities';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { useUpdateTagebuchEinstellungen } from '@/domain/controlling/hooks/useUpdateTagebuchEinstellungen';
import { useEinstellungenSheetContext } from '@/domain/controlling/pages/Umsatzplanung/Einstellungen/EinstellungenSheetContext/useEinstellungenSheetContext';
import { updateTagebuchEinstellungenSchema } from '@/domain/controlling/pages/Umsatzplanung/Einstellungen/schema';
import { EinstellungenUpdateFormSchema } from '@/domain/controlling/pages/Umsatzplanung/Einstellungen/types';
import { formatNumber } from '@/domain/controlling/pages/Umsatzplanung/utils';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { AbsatzVorgabe as GqlAbsatzVorgabe } from '@/gql/graphql';
import { toast } from '@/hooks/use-toast';
import { maskitoParseNumber } from '@maskito/kit';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

// oxlint-disable-next-line complexity
export function useEinstellungenForm() {
  const { t } = useTranslation();
  const { currentStandort } = useStandortContext();
  const { setOpen } = useEinstellungenSheetContext();
  const { updateTagebuchEinstellungen, updateUmsatzplanungAnzeige } =
    useUpdateTagebuchEinstellungen();

  const { umsatzplanungAnzeige: defaultAnzeige, tagebuchEinstellungen } =
    useTagebuchEinstellungen(currentStandort.id);

  const form = useAppForm({
    defaultValues: {
      umsatzplanungAnzeige: defaultAnzeige ?? 'UMSATZ_PRODUKTIVITAET',
      zielProduktivitaetUmsatz: formatNumber(
        tagebuchEinstellungen?.umsatzProduktivitaet.zielProduktivitaet.toString() ??
          '',
        false,
      ),
      zielProduktivitaetAbsatz: {
        value: formatNumber(
          tagebuchEinstellungen?.absatzProduktivitaet.zielProduktivitaet.toString() ??
            '',
          true,
        ),
        unit:
          tagebuchEinstellungen?.absatzProduktivitaet.absatzVorgabe ??
          AbsatzVorgabe.Stueck,
      },
    } satisfies EinstellungenUpdateFormSchema as EinstellungenUpdateFormSchema,
    validators: { onChange: updateTagebuchEinstellungenSchema },
    // oxlint-disable-next-line complexity
    onSubmit: async ({ value }) => {
      try {
        const result = await updateTagebuchEinstellungen({
          tagebuchEinstellungenUpdateInput: {
            standortId: currentStandort.id,
            umsatzProduktivitaet: {
              zielProduktivitaet: maskitoParseNumber(
                value.zielProduktivitaetUmsatz ?? '',
                { decimalSeparator: ',' },
              ),
            },
            absatzProduktivitaet: {
              zielProduktivitaet: maskitoParseNumber(
                value.zielProduktivitaetAbsatz?.value ?? '',
                { decimalSeparator: ',' },
              ),
              absatzVorgabe:
                value.zielProduktivitaetAbsatz?.unit === AbsatzVorgabe.Mpt
                  ? GqlAbsatzVorgabe.Mpt
                  : GqlAbsatzVorgabe.Stueck,
            },
          },
        });
        if (result.error) {
          throw new Error(result.error.message);
        }
        updateUmsatzplanungAnzeige(value.umsatzplanungAnzeige);
        toast({
          title: t(
            'domain.controlling.umsatzplanung.einstellungen.update.success.title',
          ),
        });
        setOpen(false);
      } catch (error) {
        toast({
          description: t(
            'domain.controlling.umsatzplanung.einstellungen.update.error.description',
          ),
        });
        Sentry.captureException(error);
      }
    },
  });

  return form;
}

export type EinstellungenForm = ReturnType<typeof useEinstellungenForm>;
